class InfiniteScrollBlog extends HTMLElement {
  constructor() {
    super();
    this.blogHandle = this.dataset.blogHandle;
    this.currentPage = parseInt(this.dataset.currentPage);
    this.totalPages = parseInt(this.dataset.totalPages);
    this.sectionId = this.dataset.sectionId;
    this.imageHeight = this.dataset.imageHeight;
    this.isLoading = false;
    
    this.articlesContainer = this.querySelector(`#blog-articles-${this.sectionId}`);
    this.loadMoreButton = this.querySelector('[data-load-more]');
    this.loadMoreText = this.querySelector('.blog-load-more__text');
    this.loadMoreSpinner = this.querySelector('.blog-load-more__spinner');
    
    this.init();
  }
  
  init() {
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.loadMoreArticles.bind(this));
    }
    
    // Hide load more button if we're on the last page
    if (this.currentPage >= this.totalPages) {
      this.hideLoadMoreButton();
    }
    
    // If we landed on a page > 1, load all previous pages first
    if (this.currentPage > 1) {
      this.loadPreviousPages();
    }
  }
  
  async loadPreviousPages() {
    if (this.currentPage <= 1) return;
    
    this.setLoadingState(true);
    
    try {
      // Create a container to hold all previous pages' articles
      const previousArticles = [];
      
      // Load all pages from 1 to currentPage - 1
      for (let page = 1; page < this.currentPage; page++) {
        const url = page === 1 ? window.location.pathname : `${window.location.pathname}?page=${page}`;
        
        const response = await fetch(url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const articlesContainer = doc.querySelector(`#blog-articles-${this.sectionId}`);
        if (articlesContainer) {
          const articles = articlesContainer.querySelectorAll('.blog-articles__article');
          articles.forEach(article => {
            // Remove any existing animation attributes since we're prepending
            if (article.classList.contains('scroll-trigger')) {
              article.classList.remove('scroll-trigger', 'animate--slide-in');
              article.removeAttribute('data-cascade');
              article.removeAttribute('style');
            }
            previousArticles.push(article.cloneNode(true));
          });
        }
      }
      
      // Prepend all previous articles to the current container
      previousArticles.reverse(); // Reverse to maintain correct order when prepending
      previousArticles.forEach(article => {
        this.articlesContainer.insertBefore(article, this.articlesContainer.firstChild);
      });
      
      // Update the URL to remove the page parameter since we now show all articles
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
    } catch (error) {
      console.error('Error loading previous pages:', error);
    } finally {
      this.setLoadingState(false);
    }
  }
  
  async loadMoreArticles() {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }
    
    this.setLoadingState(true);
    
    try {
      const nextPage = this.currentPage + 1;
      const url = `${window.location.pathname}?page=${nextPage}`;
      
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the articles container in the response
      const newArticlesContainer = doc.querySelector(`#blog-articles-${this.sectionId}`);
      
      if (newArticlesContainer) {
        const newArticles = newArticlesContainer.querySelectorAll('.blog-articles__article');
        
        // Append new articles to the existing container
        newArticles.forEach((article, index) => {
          // Add animation delay for staggered effect
          if (article.classList.contains('scroll-trigger')) {
            article.style.setProperty('--animation-order', this.getNextAnimationOrder() + index);
          }
          this.articlesContainer.appendChild(article);
        });
        
        // Update current page
        this.currentPage = nextPage;
        this.dataset.currentPage = nextPage;
        
        // Keep URL clean (without page parameter) for infinite scroll
        // This ensures that if someone refreshes or shares the URL, they get the full experience
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        
        // Trigger scroll animations if enabled
        if (window.theme && window.theme.initializeScrollAnimationTrigger) {
          window.theme.initializeScrollAnimationTrigger(this.articlesContainer);
        }
        
        // Hide load more button if we've reached the last page
        if (this.currentPage >= this.totalPages) {
          this.hideLoadMoreButton();
        }
      }
      
    } catch (error) {
      console.error('Error loading more articles:', error);
      this.showError();
    } finally {
      this.setLoadingState(false);
    }
  }
  
  setLoadingState(loading) {
    this.isLoading = loading;
    
    if (this.loadMoreButton) {
      this.loadMoreButton.disabled = loading;
      this.loadMoreButton.setAttribute('aria-busy', loading);
    }
    
    if (this.loadMoreText && this.loadMoreSpinner) {
      if (loading) {
        this.loadMoreText.textContent = 'Loading...';
        this.loadMoreSpinner.classList.remove('hidden');
      } else {
        this.loadMoreText.textContent = this.loadMoreText.dataset.originalText || 'Load More';
        this.loadMoreSpinner.classList.add('hidden');
      }
    }
  }
  
  hideLoadMoreButton() {
    const loadMoreContainer = this.querySelector('.blog-load-more');
    if (loadMoreContainer) {
      loadMoreContainer.style.display = 'none';
    }
  }
  
  showError() {
    if (this.loadMoreText) {
      this.loadMoreText.textContent = 'Error loading articles. Please try again.';
      setTimeout(() => {
        this.loadMoreText.textContent = this.loadMoreText.dataset.originalText || 'Load More';
      }, 3000);
    }
  }
  
  getNextAnimationOrder() {
    const existingArticles = this.articlesContainer.querySelectorAll('.blog-articles__article');
    return existingArticles.length + 1;
  }
  
  connectedCallback() {
    // Store original button text
    if (this.loadMoreText) {
      this.loadMoreText.dataset.originalText = this.loadMoreText.textContent;
    }
  }
}

// Register the custom element
customElements.define('infinite-scroll-blog', InfiniteScrollBlog);
